import React, { lazy, Suspense, useState, useEffect } from 'react';
import "react-toastify/dist/ReactToastify.css";
import './App.css';
import './styles/classic-light.css';
import { BrowserRouter, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { ToastContainer } from "react-toastify";
import { HelmetProvider } from 'react-helmet-async';
import { ThemeProvider } from './contexts/ThemeContext';
import { SiteProvider } from './contexts/SiteConfig';
import { AuthProvider } from './contexts/Auth';
import CustomerProvider, { useCustomer } from "./contexts/CustomerAuth";
import { useCartCleanup } from './hooks/useCartCleanup';
import Navbar from './components/Navbar';
import { AdminGuard, PageGate } from './components/RouteGuard';
import LoadingFallback from './components/LoadingFallback';
import Home from './pages/Home';

// Lazy — keeps these out of the main bundle (not needed for initial paint)
const DynamicPopup = lazy(() => import('./components/DynamicPopup'));
const Footer = lazy(() => import('./components/Footer'));

// Lazy load AdminLayout so AdminSidebar stays out of the main bundle
const AdminLayout = lazy(() => import('./components/AdminLayout'));

// Lazy load all other page components
const Catalog = lazy(() => import('./pages/Catalog'));
const BookDetail = lazy(() => import('./pages/BookDetail'));
const Cart = lazy(() => import('./pages/Cart'));
const Checkout = lazy(() => import('./pages/Checkout'));
const CustomerAuth = lazy(() => import('./pages/CustomerAuth'));
const CustomerProfile = lazy(() => import('./pages/CustomerProfile'));
const OrderHistory = lazy(() => import('./pages/OrderHistory'));
const Invoice = lazy(() => import('./pages/Invoice'));

// Policy pages
const AboutUs = lazy(() => import('./pages/AboutUs'));
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy'));
const ShippingPolicy = lazy(() => import('./pages/ShippingPolicy'));
const TermsAndConditions = lazy(() => import('./pages/Terms&Conditions'));
const RefundPolicy = lazy(() => import('./pages/RefundPolicy'));
const FAQ = lazy(() => import('./pages/FAQ'));
const ContactUs = lazy(() => import('./pages/ContactUs'));
const PreSchool = lazy(() => import('./pages/PreSchool'));
const Hero = lazy(() => import('./pages/parallax/Hero'));
const AllCategories = lazy(() => import('./pages/AllCategories'));
const CategoryBooks = lazy(() => import('./pages/CategoryBooks'));
const MoralStoriesPage = lazy(() => import('./pages/moral/index'));

// Admin pages (lazy load separately - rarely accessed)
const AdminLogin = lazy(() => import('./pages/Admin/Login'));
const AdminSetup = lazy(() => import('./pages/Admin/Setup'));
const Dashboard = lazy(() => import('./pages/Admin/Dashboard'));
const AddBook = lazy(() => import('./pages/Admin/AddBook'));
const EditBook = lazy(() => import('./pages/Admin/EditBook'));
const BooksAdmin = lazy(() => import('./pages/Admin/Books'));
const AdminOrders = lazy(() => import('./pages/Admin/Orders'));
const ShiprocketPage = lazy(() => import('./pages/Admin/ShiprocketPage'));
const SiteSettings = lazy(() => import('./pages/Admin/SiteSettings'));
const HomepageAdmin = lazy(() => import('./pages/Admin/Homepage'));
const PaymentsAdmin = lazy(() => import('./pages/Admin/Payments'));
const CatalogSettings = lazy(() => import('./pages/Admin/CatalogSettings'));
const ApiUsers = lazy(() => import('./pages/Admin/ApiUsers'));
const EmailSenders = lazy(() => import('./pages/Admin/EmailSenders'));
const EmailTemplates = lazy(() => import('./pages/Admin/EmailTemplates'));
const AdminCategories = lazy(() => import('./pages/Admin/Categories'));
const PopupSettings = lazy(() => import('./pages/Admin/PopupSettings'));
const AiSettings = lazy(() => import('./pages/Admin/AiSettings'));
const SeoSettings = lazy(() => import('./pages/Admin/SeoSettings'));
const ShippingRules = lazy(() => import('./pages/Admin/ShippingRules'));
const Coupons = lazy(() => import('./pages/Admin/Coupons'));
const AdminReviews = lazy(() => import('./pages/Admin/Reviews'));
const DataBackup = lazy(() => import('./pages/Admin/DataBackup'));
const TickerSettings = lazy(() => import('./pages/Admin/TickerSettings'));

function InnerApp() {
  const loc = useLocation();
  const showFooter = loc.pathname === '/' || loc.pathname === '/catalog' || loc.pathname === '/aboutus' || loc.pathname === '/privacy' || loc.pathname === '/shipping' || loc.pathname === '/terms' || loc.pathname === '/refund' || loc.pathname === '/faq' || loc.pathname === '/contact' || loc.pathname === '/PreSchool' || loc.pathname === '/categories' || loc.pathname.startsWith('/category/') || loc.pathname === '/moral';
  const hideNavbarRoutes = ['/intro', '/gita', '/scroll', '/admin/login'];
  const showNavbar = !hideNavbarRoutes.includes(loc.pathname);
  const isAdminRoute = loc.pathname.startsWith('/admin');

  // Defer popup mount by 3s — keeps it out of the TBT window entirely
  const [mountPopup, setMountPopup] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setMountPopup(true), 3000);
    return () => clearTimeout(t);
  }, []);

  const getPageName = () => {
    if (loc.pathname === '/') return 'hom e';
    if (loc.pathname === '/catalog') return 'products';
    if (loc.pathname === '/cart') return 'cart';
    return 'all';
  };

  function RequireCustomer({ children }) {
    const { isCustomer } = useCustomer();
    const here = useLocation();
    if (!isCustomer) return <Navigate to="/login" state={{ next: here.pathname }} replace />;
    return children;
  }

  return (
    <>
      {showNavbar && <Navbar />}
      <main>
        {!isAdminRoute && mountPopup && (
          <Suspense fallback={null}>
            <DynamicPopup page={getPageName()} />
          </Suspense>
        )}
        <Suspense fallback={<LoadingFallback />}>
          <Routes>
            {/* customer auth */}
            <Route path="/login" element={<CustomerAuth />} />
            {/* public pages gated by Visibility */}
            <Route path="/" element={<PageGate page="home"><Home /></PageGate>} />
            <Route path="/catalog" element={<PageGate page="catalog"><Catalog /></PageGate>} />
            <Route path="/aboutus" element={<PageGate page="aboutus"><AboutUs /></PageGate>} />
            <Route path="/preschool" element={<PageGate page="preschool"><PreSchool /></PageGate>} />
            <Route path="/invoice/:id" element={<Invoice />} />
            <Route path="/Sacred Stories" element={<Hero />} />
            <Route path="/profile" element={<RequireCustomer><CustomerProfile /></RequireCustomer>} />
            <Route
              path="/profile/orders"
              element={
                <RequireCustomer>
                  <OrderHistory />
                </RequireCustomer>
              }
            />
            <Route
              path="/checkout"
              element={
                <PageGate page="catalog"><Checkout /></PageGate>
              }
            />
            <Route path="/book/:slug" element={<BookDetail />} />
            <Route path="/categories" element={<AllCategories />} />
            <Route path="/category/:slug" element={<CategoryBooks />} />
            <Route path="/moral" element={<MoralStoriesPage />} />
            <Route
              path="/cart"
              element={
                <RequireCustomer>
                  <PageGate page="cart"><Cart /></PageGate>
                </RequireCustomer>
              }
            />

            {/* others */}
            <Route path="/faq" element={<PageGate page="faq"><FAQ /></PageGate>} />
            <Route path="/contact" element={<PageGate page="contact"><ContactUs /></PageGate>} />

            {/* Policy */}
            <Route path='/privacy' element={<PrivacyPolicy />} />
            <Route path='/shipping' element={<ShippingPolicy />} />
            <Route path='/terms' element={<TermsAndConditions />} />
            <Route path='/refund' element={<RefundPolicy />} />

            {/* admin auth pages */}
            <Route path="/admin/login" element={<PageGate page="adminLogin"><AdminLogin /></PageGate>} />
            <Route path="/admin/setup" element={<AdminSetup />} />

            {/* admin-only */}
            <Route
              path="/admin/api-users"
              element={<AdminGuard><AdminLayout><ApiUsers /></AdminLayout></AdminGuard>}
            />
            <Route
              path="/admin/dashboard"
              element={<AdminGuard><AdminLayout><Dashboard /></AdminLayout></AdminGuard>}
            />
            <Route
              path="/admin/orders"
              element={<AdminGuard><AdminLayout><AdminOrders /></AdminLayout></AdminGuard>}
            />
            <Route
              path="/admin/shiprocket"
              element={<AdminGuard><AdminLayout><ShiprocketPage /></AdminLayout></AdminGuard>}
            />
            <Route
              path="/admin/books"
              element={<AdminGuard><AdminLayout><BooksAdmin /></AdminLayout></AdminGuard>}
            />
            <Route
              path="/admin/books/:slug/edit"
              element={<AdminGuard><AdminLayout><EditBook /></AdminLayout></AdminGuard>}
            />
            <Route
              path="/admin/settings"
              element={<AdminGuard><AdminLayout><SiteSettings /></AdminLayout></AdminGuard>}
            />
            <Route
              path="/admin/homepage"
              element={<AdminGuard><AdminLayout><HomepageAdmin /></AdminLayout></AdminGuard>}
            />
            <Route
              path="/admin/payments"
              element={<AdminGuard><AdminLayout><PaymentsAdmin /></AdminLayout></AdminGuard>}
            />
            <Route
              path="/admin/catalog"
              element={<AdminGuard><AdminLayout><CatalogSettings /></AdminLayout></AdminGuard>}
            />
            <Route
              path="/admin/add-book"
              element={<AdminGuard><AdminLayout><AddBook /></AdminLayout></AdminGuard>}
            />
            <Route
              path="/admin/email-senders"
              element={<AdminGuard><AdminLayout><EmailSenders /></AdminLayout></AdminGuard>}
            />
            <Route
              path="/admin/email-templates"
              element={<AdminGuard><AdminLayout><EmailTemplates /></AdminLayout></AdminGuard>}
            />
            <Route
              path="/admin/categories"
              element={<AdminGuard><AdminLayout><AdminCategories /></AdminLayout></AdminGuard>}
            />
            <Route
              path="/admin/settings/popup"
              element={<AdminGuard><AdminLayout><PopupSettings /></AdminLayout></AdminGuard>}
            />
            <Route
              path="/admin/ai-settings"
              element={<AdminGuard><AdminLayout><AiSettings /></AdminLayout></AdminGuard>}
            />
            <Route
              path="/admin/seo"
              element={<AdminGuard><AdminLayout><SeoSettings /></AdminLayout></AdminGuard>}
            />
            <Route
              path="/admin/shipping-rules"
              element={<AdminGuard><AdminLayout><ShippingRules /></AdminLayout></AdminGuard>}
            />
            <Route
              path="/admin/coupons"
              element={<AdminGuard><AdminLayout><Coupons /></AdminLayout></AdminGuard>}
            />
            <Route
              path="/admin/reviews"
              element={<AdminGuard><AdminLayout><AdminReviews /></AdminLayout></AdminGuard>}
            />
            <Route
              path="/admin/data-backup"
              element={<AdminGuard><AdminLayout><DataBackup /></AdminLayout></AdminGuard>}
            />
            <Route
              path="/admin/ticker"
              element={<AdminGuard><AdminLayout><TickerSettings /></AdminLayout></AdminGuard>}
            />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Suspense>
      </main>

      {
        showFooter && (
          <Suspense fallback={null}>
            <Footer
              contact={{
                email: "kiddosintellect@gmail.com",
                phone: "+91 98798 57529",
                hours: "Mon–Sat, 10am–6pm IST",
              }}
              links={[
                { label: "Privacy", href: "/privacy" },
                { label: "Returns", href: "/returns" },
                { label: "Shipping", href: "/shipping" },
              ]}
            />
          </Suspense>
        )
      }
    </>
  );
}

export default function App() {
  useCartCleanup();
  return (
    <HelmetProvider>
      <SiteProvider>
        <AuthProvider>
          <CustomerProvider>
            <ThemeProvider>
              <BrowserRouter>
                <InnerApp />
                <ToastContainer
                  position="top-right"
                  autoClose={3000}
                  hideProgressBar
                  newestOnTop
                  closeOnClick={false}
                  draggable
                  pauseOnHover
                  closeButton={false}
                />
              </BrowserRouter>
            </ThemeProvider>
          </CustomerProvider>
        </AuthProvider>
      </SiteProvider>
    </HelmetProvider>
  );
}